import { fetchApi } from '../api';
import type { OrderTrackingDashboardData, OrderTrackingOrderItem } from './orderTracking.types';

export interface OrderTrackingDashboardParams {
  warehouse_id?: number;
  /** Fecha pedido desde (YYYY-MM-DD), inclusive. */
  order_date_from?: string;
  /** Fecha pedido hasta (YYYY-MM-DD), inclusive. */
  order_date_to?: string;
}

function normalizeOrderItem(raw: OrderTrackingOrderItem & Record<string, unknown>): OrderTrackingOrderItem {
  return {
    ...raw,
    expectedReceivedDate:
      raw.expectedReceivedDate ??
      (raw.expected_received_date as string | null | undefined) ??
      null,
    receivedDate:
      raw.receivedDate ?? (raw.received_date as string | null | undefined) ?? null,
  };
}

function normalizeOrderItemsByOrderId(
  raw: Record<string | number, OrderTrackingOrderItem[]> | undefined
): Record<number, OrderTrackingOrderItem[]> {
  if (!raw) return {};
  return Object.entries(raw).reduce<Record<number, OrderTrackingOrderItem[]>>((acc, [key, items]) => {
    acc[Number(key)] = items.map((item) =>
      normalizeOrderItem(item as OrderTrackingOrderItem & Record<string, unknown>)
    );
    return acc;
  }, {});
}

function buildQueryParams(params?: OrderTrackingDashboardParams): Record<string, string | number> | undefined {
  if (!params) return undefined;
  const query: Record<string, string | number> = {};
  if (params.warehouse_id != null) query.warehouse_id = params.warehouse_id;
  if (params.order_date_from) query.order_date_from = params.order_date_from;
  if (params.order_date_to) query.order_date_to = params.order_date_to;
  return Object.keys(query).length > 0 ? query : undefined;
}

class OrderTrackingService {
  async getDashboardData(
    params?: OrderTrackingDashboardParams
  ): Promise<OrderTrackingDashboardData> {
    const data = await fetchApi<OrderTrackingDashboardData>('order-tracking/dashboard/', {
      params: buildQueryParams(params),
    });
    return {
      ...data,
      orderItemsByOrderId: normalizeOrderItemsByOrderId(data.orderItemsByOrderId),
    };
  }
}

export const orderTrackingService = new OrderTrackingService();
